import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import consumer from "../channels/consumer";
import GenerateAIModal from "../components/google-ads/GenerateAIModal";
import ReportsList from "../components/google-ads/ReportsList";
import GenerationQueuePanel from "../components/google-ads/GenerationQueuePanel";
import ConnectGoogleAdsModal from "../components/google-ads/ConnectGoogleAdsModal";
import SettingsModal from "../components/google-ads/SettingsModal";

const getBadgeClass = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "draft") return "meta-ads-ai-page__card-badge--draft";
  if (s === "removed" || s === "archived") return "meta-ads-ai-page__card-badge--archived";
  if (s === "paused") return "meta-ads-ai-page__card-badge--paused";
  if (s === "active" || s === "enabled") return "meta-ads-ai-page__card-badge--active";
  if (s === "scheduled") return "meta-ads-ai-page__card-badge--scheduled";
  if (s === "published") return "meta-ads-ai-page__card-badge--published";
  return "meta-ads-ai-page__card-badge--draft";
};

const CampaignCardGrid = ({ campaigns, onNavigateToCampaign }) => (
  <div className="meta-ads-ai-page__card-grid">
    {campaigns.map((c) => {
      const adGroups = c.ad_groups || [];
      return (
        <div
          key={c.id}
          className="meta-ads-ai-page__card"
          onClick={() => onNavigateToCampaign(c.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && onNavigateToCampaign(c.id)}
        >
          <div className="meta-ads-ai-page__card-row">
            <div>
              <h3 className="meta-ads-ai-page__card-title">{c.name}</h3>
              <p className="meta-ads-ai-page__card-event">{c.event_name || "—"}</p>
              <div className="meta-ads-ai-page__card-meta">
                <span className={`meta-ads-ai-page__card-badge ${getBadgeClass(c.status)}`}>
                  {(c.status || "draft").toLowerCase()}
                </span>
                <span className="meta-ads-ai-page__card-ads-count">
                  {adGroups.length} ad group(s) • {c.ads_count ?? 0} ad(s)
                </span>
              </div>
            </div>
            <div className="meta-ads-ai-page__card-actions">
              <button
                type="button"
                className="meta-ads-ai-page__card-btn-view"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigateToCampaign(c.id);
                }}
              >
                View
              </button>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const STATUS_FILTERS = [
  { value: "all", label: "All Campaigns" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "draft", label: "Draft" },
  { value: "removed", label: "Removed" },
];

const GoogleAdsAIPage = () => {
  const navigate = useNavigate();
  const rootElement = document.getElementById("react-root");
  const [companyId, setCompanyId] = useState(null);
  const [hasGoogleAdsAccounts, setHasGoogleAdsAccounts] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [viewMode, setViewMode] = useState("events");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isAIDraftOpen, setIsAIDraftOpen] = useState(false);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (rootElement) {
      setCompanyId(rootElement.getAttribute("company-id"));
      const hasAccounts = rootElement.getAttribute("has-google-ads-accounts") === "true";
      setHasGoogleAdsAccounts(hasAccounts);
      setIsAdmin(rootElement.getAttribute("is-admin") === "true");
      if (!hasAccounts) setConnectModalOpen(true);
    }
  }, []);

  useEffect(() => {
    const generating = sessionStorage.getItem("googleAdsAI_generating") === "true";
    const startTime = sessionStorage.getItem("googleAdsAI_generation_start");
    const MAX_MINUTES = 5;
    if (generating && startTime) {
      const elapsed = Date.now() - parseInt(startTime, 10);
      if (elapsed > MAX_MINUTES * 60 * 1000) {
        sessionStorage.removeItem("googleAdsAI_generating");
        sessionStorage.removeItem("googleAdsAI_report_id");
        sessionStorage.removeItem("googleAdsAI_generation_start");
      } else {
        setIsGenerating(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!companyId || !hasGoogleAdsAccounts) {
      setCampaignsLoaded(true);
      setCampaigns([]);
      return;
    }
    const url = selectedEvent ? `/api/google_ads/campaigns?event_id=${selectedEvent.id}` : "/api/google_ads/campaigns";
    setCampaignsLoaded(false);
    fetch(url, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : { campaigns: [] }))
      .then((data) => {
        setCampaigns(data.campaigns || []);
        setCampaignsLoaded(true);
      })
      .catch(() => {
        setCampaigns([]);
        setCampaignsLoaded(true);
      });
  }, [companyId, hasGoogleAdsAccounts, selectedEvent?.id]);

  const refreshCampaigns = () => {
    if (!companyId || !hasGoogleAdsAccounts) return;
    const url = selectedEvent ? `/api/google_ads/campaigns?event_id=${selectedEvent.id}` : "/api/google_ads/campaigns";
    fetch(url, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : { campaigns: [] }))
      .then((data) => setCampaigns(data.campaigns || []))
      .catch(() => {});
  };

  const refreshCampaignsRef = useRef(refreshCampaigns);
  refreshCampaignsRef.current = refreshCampaigns;

  // ActionCable subscription for Google Ad publish progress/errors (when on list page)
  useEffect(() => {
    if (!consumer || !companyId) return;
    const alertConfig = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
    };
    const subscription = consumer.subscriptions.create(
      { channel: "CompanyPostsChannel", company_id: companyId },
      {
        received(data) {
          try {
            if (data?.action === "ad_publish_progress") {
              toast.info(data.message || "Publishing...", { ...alertConfig, autoClose: 3000 });
              refreshCampaignsRef.current?.();
            }
            if (data?.action === "ad_publish_error") {
              toast.error(data.message || "Failed to publish ad", alertConfig);
              refreshCampaignsRef.current?.();
            }
          } catch (err) {
            console.error("[ActionCable] Google Ads received:", err);
          }
        },
      }
    );
    return () => subscription?.unsubscribe();
  }, [companyId]);

  const campaignsByEvent = useMemo(() => {
    const grouped = {};
    campaigns.forEach((c) => {
      const eventId = c.event_id ?? "none";
      const eventName = c.event_name || "No event";
      if (!grouped[eventId]) {
        grouped[eventId] = { id: eventId, name: eventName, campaigns: [] };
      }
      grouped[eventId].campaigns.push(c);
    });
    return Object.values(grouped);
  }, [campaigns]);

  const normalizedStatus = (s) => {
    const v = (s || "").toLowerCase();
    return v === "enabled" ? "active" : v;
  };

  const filteredCampaigns = useMemo(() => {
    const list = campaigns || [];
    if (statusFilter === "all") return list;
    return list.filter((c) => normalizedStatus(c.status) === statusFilter);
  }, [campaigns, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: campaigns.length, active: 0, paused: 0, draft: 0, removed: 0 };
    campaigns.forEach((c) => {
      const s = normalizedStatus(c.status);
      if (counts[s] !== undefined) counts[s]++;
    });
    return counts;
  }, [campaigns]);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setViewMode("campaigns");
  };

  const handleBackToEvents = () => {
    setSelectedEvent(null);
    setViewMode("events");
  };

  const handleCreateCampaign = () => {
    navigate("/marketing/google_ads_ai/campaign");
  };

  return (
    <div className={`marketing meta-ads-ai-page google-ads-ai-page ${connectModalOpen ? "blurred" : ""}`}>
      <ConnectGoogleAdsModal isOpen={connectModalOpen} onClose={() => setConnectModalOpen(false)} />
      <GenerateAIModal isOpen={isAIDraftOpen} onClose={() => setIsAIDraftOpen(false)} onGenerate={() => setIsGenerating(true)} />
      <ReportsList isOpen={queueModalOpen} onClose={() => setQueueModalOpen(false)} isAdmin={isAdmin} />
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} isAdmin={isAdmin} />
      <GenerationQueuePanel
        isAdmin={isAdmin}
        onComplete={() => {
          setIsGenerating(false);
          sessionStorage.removeItem("googleAdsAI_generating");
          sessionStorage.removeItem("googleAdsAI_generation_start");
          sessionStorage.removeItem("googleAdsAI_report_id");
          refreshCampaigns();
        }}
      />

      <div className="meta-ads-dashboard-page">
        <div className="container-fluid px-4">
          <nav className="meta-ads-ai-page__breadcrumb" aria-label="Breadcrumb">
            <span className="meta-ads-ai-page__breadcrumb-items">
              {viewMode === "campaigns" ? (
                <button type="button" className="meta-ads-ai-page__breadcrumb-link meta-ads-ai-page__breadcrumb-btn" onClick={handleBackToEvents}>
                  Events
                </button>
              ) : (
                <span className="meta-ads-ai-page__breadcrumb-text meta-ads-ai-page__breadcrumb-current">Events</span>
              )}
              {viewMode === "campaigns" && selectedEvent && (
                <>
                  <span className="meta-ads-ai-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                  <span className="meta-ads-ai-page__breadcrumb-text meta-ads-ai-page__breadcrumb-current" title={selectedEvent.name}>
                    {selectedEvent.name?.length > 50 ? selectedEvent.name.slice(0, 50) + "..." : selectedEvent.name}
                  </span>
                  <span className="meta-ads-ai-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                  <span className="meta-ads-ai-page__breadcrumb-text">Campaigns</span>
                </>
              )}
            </span>
            {isAdmin && (
              <span className="admin-only-legend meta-ads-ai-page__breadcrumb-legend">
                <span className="admin-only-dot" /> = Admin only
              </span>
            )}
          </nav>

          <div className="meta-ads-dashboard__header meta-ads-ai-page__header-wrap">
            <div className="meta-ads-dashboard__title-row meta-ads-ai-page__title-row">
              <h1 className="meta-ads-dashboard__title meta-ads-ai-page__page-title">Google Ads Dashboard</h1>
              <div className="meta-ads-dashboard__header-actions meta-ads-ai-page__header-actions">
                <button type="button" className="meta-ads-ai-page__btn-ai-draft" onClick={() => setIsAIDraftOpen(true)}>
                  Draft with AI
                </button>
                <button type="button" className="meta-ads-ai-page__btn-queue" onClick={() => setQueueModalOpen(true)} title="Reports">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <button type="button" className="meta-ads-ai-page__btn-settings settings-btn" onClick={() => setSettingsOpen(true)} title="Settings">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2"/></svg>
                </button>
              </div>
            </div>

            {viewMode === "campaigns" && (
              <div className="meta-ads-ai-page__filters-wrap">
                {STATUS_FILTERS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    className={`meta-ads-ai-page__filter-btn ${statusFilter === value ? "meta-ads-ai-page__filter-btn--active" : ""}`}
                    onClick={() => setStatusFilter(value)}
                  >
                    <span className="meta-ads-ai-page__filter-label">{label}</span>
                    <span className="meta-ads-ai-page__filter-count">({statusCounts[value] ?? 0})</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {viewMode === "events" ? (
            !campaignsLoaded ? (
              <div className="meta-ads-dashboard__content" style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
                Loading...
              </div>
            ) : (
              <div className="meta-ads-dashboard__content">
                <div className="meta-ads-ai-page__events-header">
                  <div className="meta-ads-ai-page__events-header-left">
                    <h2 className="meta-ads-ai-page__events-title">Your Events</h2>
                    <span className="meta-ads-ai-page__events-count">
                      {campaignsByEvent.length} event{campaignsByEvent.length !== 1 ? "s" : ""} • {campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                {campaignsByEvent.length === 0 ? (
                  <div className="meta-ads-ai-page__card-list-empty">
                    <p>No events with campaigns yet. Use &quot;Draft with AI&quot; to get started.</p>
                  </div>
                ) : (
                  <div className="meta-ads-ai-page__event-list">
                    {campaignsByEvent.map((event) => (
                      <div
                        key={event.id}
                        className="meta-ads-ai-page__event-row"
                        onClick={() => handleSelectEvent(event)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && handleSelectEvent(event)}
                      >
                        <div className="meta-ads-ai-page__event-row-name">
                          <h3>{event.name}</h3>
                        </div>
                        <div className="meta-ads-ai-page__event-row-status">
                          <span className="meta-ads-ai-page__status-badge meta-ads-ai-page__status-badge--draft">
                            {event.campaigns.length} campaign{event.campaigns.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="meta-ads-ai-page__event-row-actions">
                          <span className="meta-ads-ai-page__event-row-view">View →</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="meta-ads-dashboard__content">
              <div className="row">
                <div className="col-12">
                  {filteredCampaigns.length > 0 ? (
                    <CampaignCardGrid
                      campaigns={filteredCampaigns}
                      onNavigateToCampaign={(id) => navigate(`/marketing/google_ads_ai/campaigns/${id}`)}
                    />
                  ) : (
                    <div className="meta-ads-ai-page__card-list-empty">
                      <p>No campaigns match the current filter.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleAdsAIPage;
